import { create } from 'zustand'
import type { Loan } from '@/types'

/** Bottom-nav pages of the Property Dossier (mockup-aligned). */
export type DealDossierTab =
  | 'overview'
  | 'property'
  | 'economics'
  | 'underwriting'
  | 'capital_stack'
  | 'parties'
  | 'documents'
  | 'journal'
  | 'audit'
  | 'field_map'

/** Tabs from the previous dossier chrome — still accepted on open. */
export type LegacyDealDossierTab =
  // The full field catalogue had its own tab; 34 of its 115 fields lived
  // nowhere else, so it was retired by moving each group to the tab that owns
  // its subject rather than by deleting it. A stored or linked 'record' lands
  // on Overview, where Deal Identity, The Request and Workflow now live.
  | 'record'
  | 'activity'
  | 'sources'
  | 'conditions'
  | 'financials'
  | 'rentroll'
  | 'comparables'
  | 'sponsor'
  | 'process'
  | 'notes'
  | 'source'
  | 'screening'
  | 'narratives'

export type DealDossierTabInput = DealDossierTab | LegacyDealDossierTab

const LEGACY_TAB: Record<LegacyDealDossierTab, DealDossierTab> = {
  record: 'overview',
  // Activity and Audit answered one question from two pages. Audit is the
  // survivor and carries the event trail as its first view.
  activity: 'audit',
  // The intake package is documents, so it reads on Documents beside the
  // deal's other files rather than behind its own tab.
  sources: 'documents',
  // Conditions had its own page; obligations and closing checklists are read
  // on Overview, which carries the conditions and obligations panels. An old
  // link still resolves rather than dead-ending.
  conditions: 'overview',
  financials: 'economics',
  rentroll: 'property',
  comparables: 'property',
  sponsor: 'parties',
  process: 'underwriting',
  notes: 'journal',
  source: 'documents',
  screening: 'underwriting',
  narratives: 'journal',
}

export function resolveDossierTab(tab: DealDossierTabInput | undefined): DealDossierTab {
  if (!tab) return 'overview'
  if (tab in LEGACY_TAB) return LEGACY_TAB[tab as LegacyDealDossierTab]
  return tab as DealDossierTab
}

interface DealDossierState {
  isOpen: boolean
  loanId: string | null
  tab: DealDossierTab
  loanSnapshot: Loan | null
  unlocked: boolean
  workspaceLoanId: string | null
  openDossier: (loanId: string, tab?: DealDossierTabInput, loan?: Loan | null) => void
  closeDossier: () => void
  setTab: (tab: DealDossierTab) => void
  setUnlocked: (unlocked: boolean) => void
  openWorkspace: (loanId?: string, loan?: Loan | null) => void
  closeWorkspace: () => void
}

export const useDealDossierStore = create<DealDossierState>((set, get) => ({
  isOpen: false,
  loanId: null,
  tab: 'overview',
  loanSnapshot: null,
  unlocked: false,
  workspaceLoanId: null,
  openDossier: (loanId, tab, loan) =>
    set({
      isOpen: true,
      loanId,
      tab: resolveDossierTab(tab),
      loanSnapshot: loan?.id === loanId ? loan : null,
      unlocked: false,
      workspaceLoanId: null,
    }),
  closeDossier: () =>
    set({
      isOpen: false,
      loanId: null,
      tab: 'overview',
      loanSnapshot: null,
      unlocked: false,
      workspaceLoanId: null,
    }),
  setTab: (tab) => set({ tab: resolveDossierTab(tab) }),
  setUnlocked: (unlocked) => set({ unlocked }),
  openWorkspace: (loanId, loan) => {
    const id = loanId ?? get().loanId
    if (!id) return
    set({
      isOpen: true,
      loanId: id,
      tab: 'overview',
      loanSnapshot: loan?.id === id ? loan : get().loanSnapshot?.id === id ? get().loanSnapshot : null,
      workspaceLoanId: null,
    })
  },
  closeWorkspace: () => set({ workspaceLoanId: null }),
}))
